/**
 * Firebase Store (Zustand)
 *
 * Manages Firebase connection state:
 * - Firestore instance
 * - Connection status
 * - Multi-tab detection (secondary tabs are read-only)
 * - User authentication state (Microsoft 365)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Firestore } from 'firebase/firestore';
import type { Database } from 'firebase/database';
import type { Messaging } from 'firebase/messaging';
import type { UserRole, LocationPermission } from '../../types';
import type { PageAccessConfig } from '../types/permissions.types';
import type { UserRoleAssignments } from '../types/roles.types';
import type { FCMTokenEntry } from '../hooks/useFCMToken';
import type { ThemePreference } from '../utils/themeBoot';

/** Notification settings stored in user document */
export interface NotificationSettings {
  enabled: boolean;
  categories?: string[];
  categoryLocations?: Record<string, string[]>;
}

/** UI preferences stored in user document */
export interface UIPreferences {
  headerPinned?: boolean;
  favoritePages?: string[];  // Array of pageIds for favorited pages
  // Light/dark/system. Mirrored into localStorage, which is what the inline
  // boot script reads before first paint — this copy exists so the choice
  // follows the user to a new device, not to drive rendering.
  theme?: ThemePreference;
}

export interface CurrentUser {
  email: string;
  displayName: string;
  role: UserRole;
  // Centralized per-page permissions (advanced mode)
  pagePermissions?: PageAccessConfig;
  // Role-based permissions (recommended mode)
  assignedRoles?: UserRoleAssignments;
  // Permission mode flag (true = use pagePermissions, false = use assignedRoles)
  useAdvancedPermissions?: boolean;
  // Legacy field for backward compatibility (rentals only)
  locationPermissions?: {
    [locationId: string]: LocationPermission;
  };
  defaultLocation?: string;
  // FCM tokens for push notifications (array of token entries with metadata)
  fcmTokens?: FCMTokenEntry[];
  // Notification preferences
  notificationSettings?: NotificationSettings;
  // UI preferences (header pinning, etc.)
  uiPreferences?: UIPreferences;
  // Division assignments (array of division IDs the user belongs to)
  divisions?: string[];
  // Company-wide Quiz manager switch (admin-set; outside pageAccess). See pages.config managerFlag
  quizManager?: boolean;
}

/** Tracks how the current Firebase auth session was established */
type AuthSource = 'none' | 'firebase_restored' | 'msal';

interface FirebaseState {
  // Firestore instance
  db: Firestore | null;

  // Realtime Database instance
  rtdb: Database | null;

  // Messaging instance
  messaging: Messaging | null;

  // Connection state
  isConnected: boolean;

  // Multi-tab detection
  isSecondaryTab: boolean;

  // Current user (Microsoft 365 authenticated)
  currentUser: CurrentUser | null;

  // User document loading state (true during initial load from Firestore)
  isUserLoading: boolean;

  // Session tracking (for heartbeat authentication)
  sessionId: string | null;

  // Auth restoration state
  // Tracks how auth was established: 'none' (not yet), 'firebase_restored' (from IndexedDB), 'msal' (full MSAL flow)
  authSource: AuthSource;
  // True while the initial Firebase persistence check is in progress (~50-100ms)
  isAuthChecking: boolean;

  // Location state
  selectedLocation: string | null;
  availableLocations: string[];
  isLoadingLocation: boolean;

  // Visibility reconnection state
  // Incremented when tab becomes visible after being hidden
  // Used to force Firestore listener re-subscription
  visibilityRefreshKey: number;

  // Actions
  setDb: (db: Firestore | null) => void;
  setRtdb: (rtdb: Database | null) => void;
  setMessaging: (messaging: Messaging | null) => void;
  setIsConnected: (connected: boolean) => void;
  setIsSecondaryTab: (isSecondary: boolean) => void;
  setCurrentUser: (user: CurrentUser | null) => void;
  setIsUserLoading: (loading: boolean) => void;
  setSessionId: (sessionId: string | null) => void;
  setAuthSource: (source: AuthSource) => void;
  setIsAuthChecking: (checking: boolean) => void;
  setSelectedLocation: (location: string) => void;
  setAvailableLocations: (locations: string[]) => void;
  setIsLoadingLocation: (loading: boolean) => void;
  incrementVisibilityRefreshKey: () => void;
}

export const useFirebaseStore = create<FirebaseState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        db: null,
        rtdb: null,
        messaging: null,
        isConnected: false,
        isSecondaryTab: false,
        currentUser: null,
        isUserLoading: true,  // Start as loading until user doc is fetched
        sessionId: null,
        authSource: 'none',
        isAuthChecking: true,  // Start true — cleared after Firebase persistence check
        selectedLocation: null,
        availableLocations: [],
        isLoadingLocation: false,
        visibilityRefreshKey: 0,

        // Actions
        setDb: (db) => set({ db }),
        setRtdb: (rtdb) => set({ rtdb }),
        setMessaging: (messaging) => set({ messaging }),
        setIsConnected: (connected) => set({ isConnected: connected }),
        setIsSecondaryTab: (isSecondary) => set({ isSecondaryTab: isSecondary }),
        setCurrentUser: (user) => set({ currentUser: user }),
        setIsUserLoading: (loading) => set({ isUserLoading: loading }),
        setSessionId: (sessionId) => set({ sessionId }),
        setAuthSource: (source) => set({ authSource: source }),
        setIsAuthChecking: (checking) => set({ isAuthChecking: checking }),
        setSelectedLocation: (location) => set({ selectedLocation: location, isLoadingLocation: true }),
        setAvailableLocations: (locations) => set({ availableLocations: locations }),
        setIsLoadingLocation: (loading) => set({ isLoadingLocation: loading }),
        incrementVisibilityRefreshKey: () => set((state) => ({ visibilityRefreshKey: state.visibilityRefreshKey + 1 })),
      }),
      {
        name: 'firebase-auth-storage',
        partialize: (state) => ({
          currentUser: state.currentUser,
          selectedLocation: state.selectedLocation,
          sessionId: state.sessionId,
        }),
      }
    ),
    { name: 'FirebaseStore' }
  )
);
